import {http as HTTP} from '../../http'
import baseConfig from '../../config'

import {get as find} from '@utils/storage'
import store from '../../vuex'

import swal from 'sweetalert2'

const devEnvironment = process.env.NODE_ENV === 'development'

const defaultPage = 1
const defaultPerPage = 20

/**
 * Função que processa a url para utilizar o padrão da API
 * @param domain - Rota do Domain da API para realizar a requisição Ex.: clients
 * @param route - Complemento de rota Ex.: clients/all
 * @param id - Id do registro
 * @param columns - Colunas do model que devem ser retornadas
 * @param relations - Relacionamentos do model que devem ser retornados
 * @param page - Página atual da paginação
 * @param perPage - Número de registros que devem ser retornados por página
 * @returns string
 */
async function _processUrl (domain, route, id, columns, relations, page, perPage) {
  let url = domain
  let hasUrlParams = false

  if (id !== undefined && id !== null) {
    url += '/' + id
  }
  url += (route ? '/' + route : '')
  url += '?q='

  // Inclui os dados da paginação na URL caso os parâmetros sejam passados
  if (page !== undefined && page !== null && page !== false) {
    page = page || defaultPage
    perPage = perPage || defaultPerPage
    url += '&_page=' + page + '&_limit=' + perPage
    hasUrlParams = true
  }

  // Inclui as colunas que devem retornar na requisição
  if (columns !== null && columns !== undefined && columns.length > 0) {
    columns.forEach(function (val) {
      if (val.length > 0) {
        url += '&_columns[]=' + val
      }
    })
    hasUrlParams = true
  }

  // Inclui os relacionamentos que devem retornar na requisição
  if (relations !== null && relations !== undefined && relations.length > 0) {
    relations.forEach(function (val) {
      if (val.length > 0) {
        url += '&_with[]=' + val
      }
    })
    hasUrlParams = true
  }
  if (!hasUrlParams) {
    url = url.replace('?q=', '')
  }
  return url
}

/**
 *
 * @param url
 * @param method
 * @param data
 * @param hideErrors
 * @returns {Promise<void>}
 */
async function logRequest (url, method, data, hideErrors) {
  const fullPath = baseConfig.fullAPIPath + url
  if (hideErrors) {
    await store.dispatch('axiosExcludeRoutesErrorMessage', [fullPath])
  }
  if (devEnvironment) {
    data = data || {}
    const token = await find('token')
    const request = {
      path: fullPath,
      auth: token ? 'Bearer ' + token : '',
      method: method,
      params: JSON.stringify(data)
    }
    console.log(`%c${request.method} ${url} `, `background:#384A5E; padding: 3px; border-radius: 3px;  color: #fff`, request)
  }
}

/**
 * Função que faz uma requisição GET na API
 * @param domain - Rota do Domain da API para realizar a requisição Ex.: clients
 * @param route - Complemento de rota Ex.: clients/all
 * @param id - Id do registro
 * @param columns - Colunas do model que devem ser retornadas
 * @param relations - Relacionamentos do model que devem ser retornados
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _get (domain, route, id, columns, relations, hideErrors) {
  await logRequest(await _processUrl(domain, route, id, columns, relations), 'GET', false, hideErrors)
  return HTTP.get(await _processUrl(domain, route, id, columns, relations)).then(response => response.data)
}

/**
 * Função que faz uma requisição POST na API
 * @param domain - Rota do Domain da API para realizar a requisição Ex.: clients
 * @param route - Complemento de rota Ex.: clients/all
 * @param id - Id do registro
 * @param data - Dados do POST
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _post (domain, route, id, data, hideErrors) {
  await logRequest(await _processUrl(domain, route, id), 'POST', data, hideErrors)
  return HTTP.post(await _processUrl(domain, route, id), data).then(response => response.data)
}

/**
 * Função que faz uma requisição PUT na API
 * @param domain - Rota do Domain da API para realizar a requisição Ex.: clients
 * @param route - Complemento de rota Ex.: clients/all
 * @param id - Id do registro
 * @param data - Dados do POST
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _put (domain, route, id, data, hideErrors) {
  await logRequest(await _processUrl(domain, route, (id || data.id)), 'PUT', data, hideErrors)
  return HTTP.put(await _processUrl(domain, route, (id || data.id)), data).then(response => response.data)
}

/**
 * Função que faz uma requisição POST/PUT na API
 * Caso existe o id no objeto data, a requisição sera PUT, caso não, será POST
 * @param domain - Rota do Domain da API para realizar a requisição Ex.: clients
 * @param route - Complemento de rota Ex.: clients/all
 * @param id - Id do registro
 * @param data - Dados do POST
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _save (domain, route, id, data, hideErrors) {
  if (data.id !== undefined && data.id !== null && data.id !== '' && data.id > 0) {
    return await _put(domain, route, id, data, hideErrors)
  }
  return await _post(domain, route, id, data, hideErrors)
}

/**
 * Função que faz uma requisição DELETE na API
 * @param domain
 * @param route
 * @param id
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _destroy (domain, route, id, hideErrors) {
  return new Promise(async (resolve, reject) => {
    swal({
      title: 'Deseja realmente remover esse item?',
      text: 'Essa ação não pode ser revertida!',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim, continuar!',
      cancelButtonText: 'Cancelar'
    })
      .then(async () => {
        await logRequest(await _processUrl(domain, route, id), 'DELETE', false, hideErrors)
        HTTP.destroy(await _processUrl(domain, route, id))
          .then(() => {
            resolve()
          })
          .catch(e => {
            reject(e)
          })
      })
      .catch(e => {
        reject()
      })
  })
}

/**
 * Busca todos os registros do domain
 * @param domain
 * @param route
 * @param id
 * @param columns
 * @param relations
 * @param page
 * @param perPage
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _all (domain, route, id, columns, relations, page, perPage, hideErrors) {
  await logRequest(await _processUrl(domain, route, id, columns, relations, page, perPage), 'GET', false, hideErrors)
  return HTTP.get(await _processUrl(domain, route, id, columns, relations, page, perPage)).then(response => response.data)
}

/**
 * Busca dinâmica de registros do domain
 * @param domain
 * @param route
 * @param data
 * @param columns
 * @param relations
 * @param page
 * @param perPage
 * @param hideErrors - Mostrar mensagens de erro
 * @returns Promise
 */
async function _search (domain, route, data, columns, relations, page, perPage, hideErrors) {
  if (data === undefined || data === null) {
    data = null
  }
  page = page || defaultPage
  perPage = perPage || defaultPerPage
  await logRequest(await _processUrl('search/' + domain, route, null, columns, relations, page, perPage), 'POST', data, hideErrors)
  return HTTP.post(await _processUrl('search/' + domain, route, null, columns, relations, page, perPage), data).then(response => response.data)
}

export const get = _get
export const post = _post
export const put = _put
export const save = _save
export const destroy = _destroy
export const all = _all
export const search = _search



// WEBPACK FOOTER //
// ./src/app/services/restfulService.js